import React from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useQuery } from "@apollo/client";
import { gettext } from "../../i18n";
import {
  GetAlarmBySourceUUID,
  GetAlarmBySourceUUID_alert,
  GetAlarmBySourceUUIDVariables,
} from "utils/__generated__/GetAlarmBySourceUUID";
import {
  GetAlarmDatasourceByUUID,
  GetAlarmDatasourceByUUID_source as Datasource,
  GetAlarmDatasourceByUUIDVariables,
} from "utils/__generated__/GetAlarmDatasourceByUUID";
import SessionTimeout from "components/common/SessionTimeout";
import { useModalContext } from "state/modal";
import {
  GET_ALARM_DATASOURCE_BY_UUID,
  GET_ALARM_BY_SOURCE_UUID,
  ALARM_LEVEL_COLORS,
  formatLimit,
} from "../../utils/alarms";
import "./ConfigureAlarmsForm.scss";

// Helper function to format recipients for display
const formatRecipients = (
  alertRecipients: any[]
): { emails: string[]; phones: string[] } => {
  if (!alertRecipients || alertRecipients.length === 0) {
    return { emails: [], phones: [] };
  }

  const emails: string[] = [];
  const phones: string[] = [];

  alertRecipients.forEach((recipient) => {
    if (recipient.email_recipient) {
      emails.push(recipient.email_recipient);
    }
    if (recipient.sms_recipient) {
      phones.push(recipient.sms_recipient);
    }
  });

  return { emails, phones };
};

const ConfigureAlarmsForm = () => {
  const { setActiveModal, selectedDataSource, setModalPayload } =
    useModalContext();
  const onHide = () => setActiveModal(null);

  const sourceUUID = selectedDataSource?.source_uuid;
  const hasDataSource = !!sourceUUID;

  const { data: sourceData, error: sourceError } = useQuery<
    GetAlarmDatasourceByUUID,
    GetAlarmDatasourceByUUIDVariables
  >(GET_ALARM_DATASOURCE_BY_UUID, {
    variables: { sourceUUID },
    skip: !hasDataSource,
  });

  const { data: alarmData } = useQuery<
    GetAlarmBySourceUUID,
    GetAlarmBySourceUUIDVariables
  >(GET_ALARM_BY_SOURCE_UUID, {
    variables: { sourceUUID },
    skip: !hasDataSource,
  });

  if (sourceError) {
    console.error("ConfigureAlarmsForm GQL error: ", sourceError);
    return <SessionTimeout />;
  }

  if (!sourceData) {
    return <React.Fragment />;
  }

  const datasource: Datasource | null | undefined = sourceData?.source;

  const alarms = alarmData?.alert || [];

  // Get the parameter name from either channel or formula
  const parameterName = datasource?.name || "";
  const parameterUnit = datasource?.unit?.symbol || "";

  const handleEditAlarm = (alarm: GetAlarmBySourceUUID_alert) => {
    // Set the alarm to edit as modal payload
    setModalPayload(alarm);
    // Switch to the add/edit alarm modal
    setActiveModal("addNewAlarm");
  };

  const handleAddNewAlarm = () => {
    // Clear any existing modal payload
    setModalPayload(null);
    setActiveModal("addNewAlarm");
  };

  return (
    <Modal
      show={true}
      onHide={onHide}
      size="xl"
      aria-labelledby="configure-alarms-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="configure-alarms-modal">
          {gettext("Configure alarms")}: {parameterName}{" "}
          {parameterUnit && `[${parameterUnit}]`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alarms-section">
          <div className="section-header">
            <h5>{gettext("Existing Alarms")}</h5>
            <Button variant="primary" size="sm" onClick={handleAddNewAlarm}>
              {gettext("Add New Alarm")}
            </Button>
          </div>

          {alarms.length === 0 ? (
            <div className="no-alarms">
              <p>{gettext("No alarms configured for this data source.")}</p>
            </div>
          ) : (
            <div className="alarm-list">
              {alarms.map((alarm) => {
                const recipients = formatRecipients(alarm.alert_recipients);
                const hasRecipients =
                  recipients.emails.length > 0 || recipients.phones.length > 0;

                return (
                  <div
                    key={alarm.id}
                    className="alarm-item"
                    onClick={() => handleEditAlarm(alarm)}
                  >
                    <div className="alarm-level">
                      <span
                        className="level-indicator"
                        style={{
                          backgroundColor: ALARM_LEVEL_COLORS[alarm.level],
                        }}
                      />
                      {gettext(alarm.level)}
                    </div>
                    <div className="alarm-limits">
                      {gettext("Lower Limit")}: {formatLimit(alarm.lower_limit)}
                    </div>
                    <div className="alarm-limits">
                      {gettext("Upper Limit")}: {formatLimit(alarm.upper_limit)}
                    </div>
                    <div className="alarm-recipients">
                      <div className="recipients-label">
                        {gettext("Recipients")}:
                      </div>
                      {!hasRecipients ? (
                        <span className="no-recipients">
                          {gettext("No recipients")}
                        </span>
                      ) : (
                        <div className="recipients-list">
                          {recipients.emails.map((email, idx) => (
                            <span
                              key={`email-${idx}`}
                              className="recipient-badge email-badge"
                            >
                              <i className="recipient-icon">✉</i>
                              {email}
                            </span>
                          ))}
                          {recipients.phones.map((phone, idx) => (
                            <span
                              key={`phone-${idx}`}
                              className="recipient-badge phone-badge"
                            >
                              <i className="recipient-icon">📱</i>
                              {phone}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {gettext("Close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfigureAlarmsForm;
